import React from 'react';
import { Sparkles, MapPin, TrendingUp, Eye, Building2, Waves, CheckCircle } from 'lucide-react';

export default function PropertyFeaturesCard({ features }) {
    if (!features) return null;

    const { visual, spatial, market, metadata } = features;

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-emerald-600" />
                    Multi-Dimensional Property Intelligence
                </h3>
                {metadata?.dataCompleteness && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                        {metadata.dataCompleteness.percentage.toFixed(0)}% Complete
                    </span>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Visual Features */}
                {visual && (
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border border-blue-200">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="p-2 bg-blue-500 rounded-lg">
                                <Eye className="w-5 h-5 text-white" />
                            </div>
                            <h4 className="text-sm font-bold text-gray-900">Visual Features</h4>
                        </div>

                        <div className="space-y-3">
                            {/* Architecture */}
                            {visual.architecture?.dominant && (
                                <div className="bg-white rounded-lg p-3">
                                    <p className="text-xs text-gray-600 mb-1">Architecture Style</p>
                                    <p className="text-sm font-bold text-gray-900 capitalize">
                                        {visual.architecture.dominant}
                                    </p>
                                    {visual.architecture.confidence && (
                                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                                            <div
                                                className="bg-blue-500 h-1.5 rounded-full"
                                                style={{ width: `${visual.architecture.confidence * 100}%` }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Azulejo Detection */}
                            {visual.azulejoPresent && (
                                <div className={`rounded-lg p-3 ${visual.azulejoPresent.present
                                        ? 'bg-emerald-100 border border-emerald-300'
                                        : 'bg-gray-100'
                                    }`}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            {visual.azulejoPresent.present ? (
                                                <CheckCircle className="w-4 h-4 text-emerald-600" />
                                            ) : (
                                                <span className="w-4 h-4 text-gray-400">○</span>
                                            )}
                                            <span className="text-xs font-medium text-gray-900">
                                                Azulejo Tiles
                                            </span>
                                        </div>
                                        {visual.azulejoPresent.present && (
                                            <span className="text-xs font-bold text-emerald-600">
                                                {(visual.azulejoPresent.confidence * 100).toFixed(0)}%
                                            </span>
                                        )}
                                    </div>
                                    {visual.azulejoPresent.present && (
                                        <p className="text-xs text-emerald-700 mt-1">
                                            🇵🇹 Portuguese cultural heritage detected
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Style Confidence */}
                            {visual.styleConfidence && (
                                <div className="text-xs text-gray-600">
                                    <span>Detection confidence: </span>
                                    <span className="font-semibold text-blue-600">
                                        {(visual.styleConfidence * 100).toFixed(0)}%
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Spatial Features */}
                {spatial && (
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 border border-green-200">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="p-2 bg-green-500 rounded-lg">
                                <MapPin className="w-5 h-5 text-white" />
                            </div>
                            <h4 className="text-sm font-bold text-gray-900">Location Features</h4>
                        </div>

                        <div className="space-y-3">
                            {/* Coast Distance */}
                            {spatial.distanceToCoast && (
                                <div className="bg-white rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Waves className="w-4 h-4 text-green-600" />
                                        <p className="text-xs text-gray-600">Distance to Coast</p>
                                    </div>
                                    <p className="text-lg font-bold text-gray-900">
                                        {spatial.distanceToCoast.distance.toFixed(1)} km
                                    </p>
                                    <span className="inline-block mt-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                                        {spatial.distanceToCoast.category}
                                    </span>
                                </div>
                            )}

                            {/* Nearest City */}
                            {spatial.nearestCity && (
                                <div className="bg-white rounded-lg p-3">
                                    <p className="text-xs text-gray-600 mb-1">Nearest Major City</p>
                                    <p className="text-sm font-bold text-gray-900 capitalize">
                                        {spatial.nearestCity.city}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {spatial.nearestCity.distance.toFixed(1)} km away
                                    </p>
                                </div>
                            )}

                            {/* Urban Category */}
                            {spatial.urbanCategory && (
                                <div className="bg-white rounded-lg p-3">
                                    <p className="text-xs text-gray-600 mb-1">Area Type</p>
                                    <span className={`inline-block px-3 py-1 text-xs font-bold rounded-full ${spatial.urbanCategory === 'urban'
                                            ? 'bg-green-500 text-white'
                                            : spatial.urbanCategory === 'suburban'
                                                ? 'bg-green-300 text-green-900'
                                                : 'bg-green-100 text-green-700'
                                        }`}>
                                        {spatial.urbanCategory.toUpperCase()}
                                    </span>
                                </div>
                            )}

                            {/* Region */}
                            {spatial.region && (
                                <div className="text-xs text-gray-600">
                                    <span>Region: </span>
                                    <span className="font-semibold text-green-700">
                                        {spatial.region}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Market Features */}
                {market && (
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-5 border border-orange-200">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="p-2 bg-orange-500 rounded-lg">
                                <TrendingUp className="w-5 h-5 text-white" />
                            </div>
                            <h4 className="text-sm font-bold text-gray-900">Market Intelligence</h4>
                        </div>

                        <div className="space-y-3">
                            {/* Market Tier */}
                            {market.marketTier && (
                                <div className="bg-white rounded-lg p-3">
                                    <p className="text-xs text-gray-600 mb-2">Market Classification</p>
                                    <div className="flex items-center gap-2">
                                        <Building2 className="w-4 h-4 text-orange-600" />
                                        <span className={`px-3 py-1 text-sm font-bold rounded-full ${market.marketTier === 'tier_1'
                                                ? 'bg-orange-500 text-white'
                                                : market.marketTier === 'tier_2'
                                                    ? 'bg-orange-300 text-orange-900'
                                                    : 'bg-orange-100 text-orange-700'
                                            }`}>
                                            {market.marketTier.toUpperCase().replace('_', ' ')}
                                        </span>
                                    </div>
                                    {market.marketTier === 'tier_1' && (
                                        <p className="text-xs text-orange-700 mt-2">
                                            🏆 Premium market area
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Listing Density */}
                            {market.listingDensity && (
                                <div className="bg-white rounded-lg p-3">
                                    <p className="text-xs text-gray-600 mb-1">Listing Activity</p>
                                    <p className="text-sm font-bold text-gray-900 capitalize">
                                        {market.listingDensity.replace('_', ' ')}
                                    </p>
                                    <div className="flex items-center gap-1 mt-2">
                                        {['very_high', 'high'].includes(market.listingDensity) ? (
                                            <>
                                                {[...Array(4)].map((_, i) => (
                                                    <div key={i} className="w-2 h-4 bg-orange-500 rounded"></div>
                                                ))}
                                            </>
                                        ) : market.listingDensity === 'medium' ? (
                                            <>
                                                {[...Array(3)].map((_, i) => (
                                                    <div key={i} className="w-2 h-4 bg-orange-400 rounded"></div>
                                                ))}
                                                <div className="w-2 h-4 bg-gray-300 rounded"></div>
                                            </>
                                        ) : (
                                            <>
                                                {[...Array(2)].map((_, i) => (
                                                    <div key={i} className="w-2 h-4 bg-orange-300 rounded"></div>
                                                ))}
                                                {[...Array(2)].map((_, i) => (
                                                    <div key={i} className="w-2 h-4 bg-gray-300 rounded"></div>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Investment Potential */}
                            {market.investmentPotential && (
                                <div className="bg-white rounded-lg p-3">
                                    <p className="text-xs text-gray-600 mb-1">Investment Signal</p>
                                    <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${market.investmentPotential === 'high'
                                            ? 'bg-green-100 text-green-700'
                                            : market.investmentPotential === 'medium'
                                                ? 'bg-yellow-100 text-yellow-700'
                                                : 'bg-gray-100 text-gray-700'
                                        }`}>
                                        {market.investmentPotential.toUpperCase()}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                    <span className="font-semibold">🔬 Phase 1 AI:</span> Multi-dimensional analysis combining visual recognition,
                    geographic intelligence, and market data for comprehensive property insights.
                </p>
            </div>
        </div>
    );
}
